// Used in profile-card.component.html and profile-card-student.component.html

// Angular
import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component,
    EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";

// Services/Types/Helpers
import { TimezoneHelper } from "#core/helpers/timezone.helper";
import { UpdateFidoProfileHelper } from "#core/helpers/update-fido-profile.helper";
import { LocalStorage } from "#core/services/local-storage.service";
import { FormHelper } from "#helpers/form.helper";
import { StatesHelper } from "#helpers/states.helper";
import { PPUser as User } from "cli-types";

// RXJS/Store
import * as fromCoursesStore from "#core/teacher-classes/store";
import * as fromStore from "#login/store";
import { Auth, DistrictsFidoService, FidoUserRequestObj, SchoolsFidoService, UpdateProfileRequest } from "@carnegie-learning/data-access-angular-passport";
import { select, Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "edit-profile",
    templateUrl: "./profile-edit.component.html",
    styleUrls: [
        "./profile-edit.component.scss",
        "../../../styles/shared-styles.scss",
    ],
    standalone: false
})
export class EditProfileComponent implements OnChanges, OnInit, OnDestroy {
    @Input() profileData?: User;

    isStudent = false;
    isTeacher = false;
    changePasswordMode = false;
    hide1 = true; // Password visibility for current Pwd
    hide2 = true; // Password visibility for new Pwd
    hide3 = true; // Password visibility for confirm Pwd
    showProgressBar = false;
    submittingForm = false;

    private ngUnsubscribe = new Subject();

    timezones = this.timezoneHelper.getTimezones();
    usStates = this.statesHelper.getStates();

    profileForm = this.fb.group({
        firstName: ["", [Validators.minLength(1), Validators.maxLength(128)]],

        lastName: ["", [Validators.minLength(2), Validators.maxLength(128)]],
        timezone: ["", [Validators.required, Validators.maxLength(255)]],
        state: ["", [Validators.maxLength(255)]],

        passwordGroup: this.fb.group(
            {
                // password: [""],
                newPassword: [""],
                confirmPassword: [""],
            },
            {
                validator: [],
            }
        ),
    });

    @Output() public processCancel = new EventEmitter<void>();

    constructor(
        private auth: Auth,
        private fb: UntypedFormBuilder,
        public store: Store<fromStore.UserState>,
        public formHelper: FormHelper,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private timezoneHelper: TimezoneHelper,
        private statesHelper: StatesHelper,
        private localStorage: LocalStorage,
        private updateFidoProfileHelper: UpdateFidoProfileHelper,
        private schoolsFidoService: SchoolsFidoService,
        private districtFidoService: DistrictsFidoService
    ) {}

    ngOnInit() {
        // TODO Refactor this out to container, replace with async validator.
        this.store
            .pipe(select(fromStore.getUpdateProfileError))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                if (data === true && this.showProgressBar) {
                    // this.profileForm.get("passwordGroup.password")?.setErrors({ invalidPassword: true });
                    this.showProgressBar = false;
                    this.cdr.detectChanges();
                }
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.profileData && !this.submittingForm) {
            this.profileForm.patchValue({
                firstName: this.profileData.firstName,
                lastName: this.profileData.lastName,
                timezone: this.profileData.timezone,
                state: this.profileData.state,
            });

            this.isStudent = this.profileData.roleLevel === 5;
            this.isTeacher = this.profileData.roleLevel === 4;
            if (
                !this.profileData.state &&
                this.isStudent &&
                this.profileData.schools?.length
            ) {
                this.schoolsFidoService
                    .getSchools(this.profileData.schools)
                    .pipe(takeUntil(this.ngUnsubscribe))
                    .subscribe((schoolData) => {
                        if (schoolData?.list.length) {
                            const school = schoolData.list[0]; // just use the first one. most likely there will only ever be 1 anyway
                            if (school?.state) {
                                this.profileForm.patchValue({
                                    state: school.state,
                                }); // if this is defined, use it
                            } else if (school.districtId) {
                                this.districtFidoService
                                    .getDistrict(school.districtId)
                                    .pipe(takeUntil(this.ngUnsubscribe))
                                    .subscribe((districtData) => {
                                        if (districtData) {
                                            this.profileForm.patchValue({
                                                state: districtData.state,
                                            }); // if no state is defined on the school, use the district
                                        }
                                    });
                            }
                        }
                    });
            }
        }

        if (this.isStudent) {
            this.profileForm.get("timezone")?.disable();
        }
    }

    markAsTouched(event: KeyboardEvent) {
        this.profileForm.markAsTouched();
    }

    validate(field: string, type: string) {
        return this.formHelper.Validate(this.profileForm, field, type);
    }

    disableUpdateButton(): boolean {
        return this.profileForm.invalid || this.profileForm.untouched;
    }

    submit() {
        this.submittingForm=true;
        const payload: UpdateProfileRequest = {
            firstName: this.profileForm.get("firstName")?.value,
            lastName: this.profileForm.get("lastName")?.value,
            timezone: this.profileForm.get("timezone")?.value,
            state: this.profileForm.get("state")?.value,
            password: this.profileForm.get("passwordGroup.password")?.value,
            newPassword: this.profileForm.get("passwordGroup.newPassword")
                ?.value,
        };
        this.showProgressBar = true;
        if (
            this.profileData &&
            (this.profileData.firstName !== payload.firstName ||
                this.profileData.lastName !== payload.lastName)
        ) {
            const updatedUserData: FidoUserRequestObj = {
                loginId: this.profileData.eAddress,
                givenName: payload.firstName,
                surName: payload.lastName,
                email: this.profileData.eAddress,
                role: this.updateFidoProfileHelper.convertRole(this.profileData.roleLevel),
                fullName: "",
            };
            this.profileData = {
                ...this.profileData,
                firstName: payload.firstName,
                lastName: payload.lastName,
            };
            this.localStorage.set("userProfile", this.profileData); // Update local storage
            this.store.dispatch(
                fromCoursesStore.updateFidoStudent({
                    fidoUserID: this.profileData.userGUID,
                    user: updatedUserData,
                })
            ); // firstName and lastName go to FIDO
        }

        this.store.dispatch(
            fromStore.userProfileUpdateAttempt({ payload: payload })
        ); // state and timezone don't go to FIDO. They just go here (pp).

        const updatedFidoPayload = {
            firstName: this.profileForm.get("firstName")?.value,
            lastName: this.profileForm.get("lastName")?.value,
        };
        this.updateFidoProfileHelper.sendUpdate(updatedFidoPayload);
    }

    cancel() {
        // Just route back to the home page; cleanest way to leave the edit profile page.
        this.router.navigate(["teacher/classes/"]);
    }
}
